import React, { PureComponent } from "react";
import clsx from "clsx";

import {
  ComposedChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  Line,
  YAxis,
  XAxis,
  CartesianGrid,
  ReferenceDot,
  Cell,
} from "recharts";

import {
  CustomizedYAxisTickLeftCurrency,
  CustomizedXAxisTickTime,
  CustomizedYAxisTickRight,
  StandardTooltip,
  selectFormat,
  Error,
  isDateFromDataInISOFormat,
  formatDateFromData,
  getAllKeys,
} from "./../Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "./../Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

const COLORS = {
  paid_digital: "#FA6E58",
  digital_spend: "#FA6E58",
  broadcast: "#2AC8DD",
  // lines
  new_customers: "#868E96",
  free_trials_started: "#868E96",
  subscription_starts: "#868E96",
};

const lines = ["subscription_starts", "new_customers"];
const marqueeEndpointURI =
  "/dashboard/marqueetv/adboard-efficiency?p=marqueetv";

class AttributionEfficiency extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
      widgetsData: props.data.data,
      widgetsInsights: [],
    };
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  renderBars = (data) => {
    let bars = [];
    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data.length <= 7;
    const labels = [];
    if (this.props.url.indexOf(marqueeEndpointURI) > -1) {
      lines[lines.indexOf("new_customers")] = "free_trials_started";
    }

    [...new Set(sortedKeys)].forEach((bar, i) => {
      if (!lines.includes(bar))
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId="stack"
            barSize={isFixedBarWidth ? 35 : null}
            fill={COLORS[bar]}
            yAxisId="left"
            isAnimationActive={false}
            cursor="pointer"
            background={
              i === 0 ? (
                <RenderBackground
                  insights={insights}
                  showIndicator={!this.state.tooltip.show}
                  node={this.node}
                  i={i}
                />
              ) : null
            }
            onClick={(d) => {
              const label = labels.filter((l) => l.date.date === d.date.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: COLORS[k],
                }))
                .filter((o) => o.value !== undefined);

              d.extra = extra;
              d.insight = label.length ? label[0] : null;

              d.posts = d.posts || [];

              this.clickBar(d, bar);
            }}
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  ></Cell>
                )
            )}
          </Bar>
        );
    });

    return bars;
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: COLORS[entry] }}
        ></span>
        <span className="legend__text" style={{ color: COLORS[entry] }}>
          {entry.replaceAll("_", " ")}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = this.getSortedKeys(data);
    sortedKeys.forEach((source) => all_legends.push(source));

    legends = [...new Set(all_legends)];
    return this.legendElement(legends);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date", "cpa"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  getExportData = () => ({
    data: this.replaceFields(this.props.data.data),
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  clickBar(d, dataKey) {
    if (
      this.state.tooltip.show &&
      d.date.date === this.state.tooltip.date.date
    ) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  replaceFields = (data) => {
    if (this.props.url.indexOf(marqueeEndpointURI) > -1) {
      data.forEach((d) => {
        d.free_trials_started = d.new_customers;
        delete d.new_customers;
      });
    }
    return data;
  };

  render() {
    const { data, title, url } = this.props;

    const { widgetsInsights } = this.state;

    const widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={370} />;
    }

    const preparedData = this.replaceFields(
      formatDateFromData(widget_data, "YYYY/MM/DD")
    );

    const lineKey = () => {
      const uniqueDataKeys = Object.keys(
        Object.assign({}, ...this.props.data.data)
      );

      if (uniqueDataKeys.includes("new_customers")) {
        return url.indexOf(marqueeEndpointURI) > -1
          ? "free_trials_started"
          : "new_customers";
      } else {
        return "subscription_starts";
      }
    };

    return (
      <div ref={this.divPosition}>
        {this.renderLegends(preparedData)}
        <div className="d-flex mt-2">
          <ResponsiveContainer height={370} width="100%">
            <ComposedChart
              data={preparedData}
              margin={{ top: 20, right: 0, bottom: 25, left: -38 }}
              stackOffset="sign"
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => {
                  return (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      yAxisId="right"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                          // tall widgets need custom height
                        />
                      }
                    />
                  );
                })}
              {!this.state.tooltip.show && (
                <Tooltip
                  // cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  {tooltip_data.date}
                                </span>
                              </header>
                              <main className="standard-tooltip__main">
                                {Object.keys(payload)
                                  .sort(
                                    (a, b) =>
                                      payload[b].value - payload[a].value
                                  )
                                  .map((key) => (
                                    <div
                                      className="standard-tooltip__item"
                                      key={key}
                                    >
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: payload[key].color,
                                        }}
                                      />
                                      <span
                                        className={clsx(
                                          "standard-tooltip__key",
                                          "text-capitalize"
                                        )}
                                        style={{ color: payload[key].color }}
                                      >
                                        {payload[key].dataKey.replaceAll(
                                          "_",
                                          " "
                                        )}
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {payload[key].dataKey ===
                                          "paid_digital" ||
                                        payload[key].dataKey === "broadcast"
                                          ? selectFormat(
                                              "currency",
                                              payload[key].value
                                            )
                                          : selectFormat(
                                              "number",
                                              payload[key].value
                                            )}
                                      </span>
                                    </div>
                                  ))}
                                {widgetsInsights.length > 0 &&
                                  !this.state.tooltip.show && (
                                    <InsightsWithinTooltip
                                      data={widgetsInsights}
                                      tooltipDate={tooltip_data.date}
                                    />
                                  )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              {this.renderBars(preparedData)}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey={lineKey()}
                stroke="#868E96"
                strokeWidth={1}
                dot={false}
                cursor="pointer"
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTime />}
              />

              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickLeftCurrency />}
              />

              <YAxis
                yAxisId="right"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickRight />}
                orientation="right"
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
        <div
          className="xray-tooltip-container"
          style={{
            top: "80px", //(this.state.tooltip.y - 20) + 'px',
            left:
              this.state.tooltip.x + this.state.tooltip.width + 220 <
              this.state.width
                ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                : this.state.tooltip.x -
                  getXrayTooltipPosition(
                    this.divPosition.current,
                    this.state.tooltip
                  ) +
                  "px",
            opacity: this.state.tooltip.show ? 1.0 : 0.0,
            visibility: this.state.tooltip.show ? "visible" : "hidden",
          }}
        >
          <XRayTooltip
            data={this.state.tooltip}
            insights={widgetsInsights}
            close={this.closeTooltip.bind(this)}
          />
        </div>
      </div>
    );
  }
}

export default AttributionEfficiency;
